import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, assetCatalog, fota, registry, type Firmware } from "@/api/client";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Field } from "@/components/Field";
import { FilePick } from "@/components/FilePick";
import { PageHead } from "@/components/PageHead";
import { useCompanyScope } from "@/auth/companyScope";
import { Modal } from "@/components/Modal";
import { StatusChip } from "@/components/StatusChip";
import { ReachMatrix, type ReachRow } from "./ReachMatrix";
// The catalogue owns kind labels; importing them beats a second copy
// of the same 13 strings (and since 0064 they come from the DATABASE,
// so a product added in Products is named correctly here for free).
import { kindLabel, useTemplates } from "@/modules/asset_catalog/common";
import type { CatalogTemplates } from "@/api/client";
import { Select, SearchInput } from "@/components/inputs";
import { POLL } from "@/lib/format";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";

import { ActionRow } from "@/components/ActionRow";
import { Icon } from "@/components/icons";
/** What each artefact ARRIVES AS. Dattatray: *"The host controller
 *  configuration file will be excel format / the host controller bootloader
 *  file will be .out format"*.
 *
 *  Three artefacts, three file types, because they reach the board three
 *  different ways: an app is flashed over CAN/UDS by the bootloader, a
 *  bootloader is written over SWD by the EOL tool (TI CCS `.out`), and a
 *  config is a spreadsheet the tool opens and never flashes.
 *
 *  A HINT, NOT THE RULE. `accept` only filters the file dialog and any person
 *  can defeat it; `store_firmware`'s allowlist is what actually decides. Kept
 *  here rather than served because it is presentation -- the label a person
 *  reads and the filter on a dialog -- and because getting it wrong costs a
 *  re-pick, not a wrong build.
 */
const FILE_KIND: Record<string, { label: string; accept: string }> = {
  app: { label: "Binary (.bin / .hex)", accept: ".bin,.hex" },
  bootloader: { label: "Bootloader (.out)", accept: ".out,.bin,.hex" },
  config: { label: "Configuration (.xlsx)", accept: ".xlsx,.xls,.csv" },
};

const SOFTWARE_LABEL: Record<string, string> = {
  host: "Host controller SW", modem_cell: "Telematics modem SW", ble: "BLE modem SW",
};

/** Node and artefact labels, READ OUT OF THE CATALOGUE rather than restated.
 *
 *  Every kind's `swtree` field already carries them -- it is what draws the
 *  product form's software tree -- so the same spec that says "Host controller"
 *  there says it here. A `Record<string,string>` in this file would be a second
 *  copy of `templates.SW_NODES` / `SW_ARTEFACTS`, and a copy of a vocabulary
 *  drifts: that is the defect this session spent the day removing from the
 *  docs, and it would be worse in a picker, where the drift shows as a build
 *  published against the wrong thing.
 *
 *  Merged across kinds because the vocabularies are GLOBAL (D-123) while a
 *  build's own kind may not be loaded -- the Builds table lists builds from
 *  every product at once.
 */
function swLabels(tpl: CatalogTemplates | undefined) {
  const node: Record<string, string> = {};
  const artefact: Record<string, string> = {};
  for (const groups of Object.values(tpl?.templates ?? {})) {
    for (const g of groups) {
      for (const f of g.fields) {
        if (f.type !== "swtree") continue;
        for (const n of f.nodes ?? []) {
          node[n.node] = n.label;
          for (const c of n.components) artefact[c.artefact] = c.label;
        }
      }
    }
  }
  return { node, artefact };
}

/** Companies a build may be given REACH to, plus anyone already given it.
 *
 *  Dattatray: *"The UI should only show those companies who have been allocted
 *  that product and model. Otherwise it does not make sense to show them"*.
 *
 *  THREE BRANCHES, and only the first is obvious -- the same shape the
 *  catalogue's visibility picker settled on, because it is the same question:
 *
 *    eligible exist      offer the eligible
 *    already granted     offer them TOO, even if entitlement was withdrawn
 *                        afterwards -- otherwise the company vanishes from the
 *                        control while its rows stay in the database, and the
 *                        operator cannot even remove what they can no longer see
 *    nothing to go on    fall back to every company, because an EMPTY picker
 *                        reads as "no companies exist" rather than "still
 *                        loading" or "you may not ask"
 */
function useReachCompanies(kind: string | null, versionId: string | null,
                           named: string[],
                           all: { company_id: string; name: string }[]) {
  const q = useQuery({
    queryKey: ["fota", "reach-companies", kind, versionId],
    queryFn: () => fota.reachableCompanies(kind as string, versionId),
    enabled: !!kind,
  });
  const eligible = q.data?.companies ?? [];
  if (!eligible.length && !named.length) return all;
  const ids = new Set(eligible.map((c) => c.company_id));
  return all.filter((c) => ids.has(c.company_id) || named.includes(c.company_id));
}

/** One node of the Builds tree: a product, a model, or a controller whose
 *  `rows` are the builds themselves. `kids` and `rows` are exclusive — a node
 *  either branches or holds versions. */
type TNode = {
  key: string; label: ReactNode; count: number;
  kids?: TNode[]; rows?: Firmware[];
};

/** One collapsible level of the Builds tree.
 *
 *  OPEN ABOVE THE LEAF, CLOSED AT IT, as a stated rule rather than a guess at
 *  what is interesting: the levels that only NAVIGATE (product, model) are
 *  open so the shape of the library is visible on arrival, and the level that
 *  mounts a table is closed so arriving does not mount every table.
 *
 *  That second half is a real constraint, not tidiness. Each leaf table is a
 *  DataTable, and every DataTable carries a ResizeObserver and its own
 *  column-measuring effect; rendering the leaf only while it is open keeps the
 *  number of live observers to the number of groups a person has actually
 *  opened, instead of one per controller in the library.
 */
function TreeNode({ n, depth, leafCols }: {
  n: TNode; depth: number; leafCols: DataCol<Firmware>[];
}) {
  const [open, setOpen] = useState(!n.rows);
  return (
    <div>
      <button className={`trow d${depth}`} onClick={() => setOpen((o) => !o)}
        aria-expanded={open}>
        <Icon name={open ? "collapse" : "expand"} size={14} />
        <span className="tlabel">{n.label}</span>
        <span className="chip mute tcount">{n.count}</span>
      </button>
      {open && (
        <div className="tkids">
          {n.rows
            // PAGED LIKE THE FLAT TABLE. A controller's version list grows by
            // one row per upload forever, so "it is only a few today" is not a
            // bound — cells are the mount budget (UI law 7) and a leaf is no
            // more exempt from it than the flat view is.
            ? <DataTable rows={n.rows} cols={leafCols} rowKey={(f) => f.id} pageSize={25} />
            : n.kids?.map((k) => (
              <TreeNode key={k.key} n={k} depth={depth + 1} leafCols={leafCols} />
            ))}
        </div>
      )}
    </div>
  );
}

/** Firmware library — its own subpage (out of Batch update): a build is
 *  (subcomponent model, software line, version, file), optionally scoped to
 *  a company. The model comes first; the Software options are exactly the
 *  FOTA methods that model's catalog ticks declare. */
export function FotaFirmwarePage() {
  const qc = useQueryClient();
  const fb = useMutationFeedback();
  const fw = useQuery({ queryKey: ["fota", "fw"], queryFn: fota.firmware, refetchInterval: POLL.LIST });
  const pool = useQuery({ queryKey: ["asset-catalog", "models", "pool"], queryFn: () => assetCatalog.models({ limit: "1000" }) });
  const companies = useQuery({ queryKey: ["registry", "companies"], queryFn: registry.companies });
  const tpl = useTemplates().data;

  const { node: NODE_LABEL, artefact: ARTEFACT_LABEL } = swLabels(tpl);

  /** A build's identity as a key and as a label (D-123).
   *
   *  NOT `software`, which is the deprecated mirror and is NULL for every
   *  bootloader and every config -- so grouping on it put all of them in one
   *  nameless bucket and the Controller filter offered a blank option. A build
   *  Orbit did not write through `register_firmware` has neither, and says so
   *  rather than being silently folded in with the host apps. */
  const swKey = (f: Firmware) => `${f.node ?? ""}/${f.artefact ?? ""}`;
  const swName = (f: Firmware) => {
    if (!f.node) return f.software ? (SOFTWARE_LABEL[f.software] ?? f.software) : "unknown";
    const n = NODE_LABEL[f.node] ?? f.node;
    const a = f.artefact ? (ARTEFACT_LABEL[f.artefact] ?? f.artefact) : "?";
    return `${n} - ${a}`;
  };

  /** What a model declares it carries: {node: [artefact, ...]} (D-123). */
  const componentsOf = (m: { derived?: unknown } | undefined) =>
    ((m?.derived as { sw_components?: Record<string, string[]> })?.sw_components) ?? {};

  // DECLARES SOFTWARE, not "declares FOTA". The filter was
  // `Array.isArray(derived.fota_methods)`, and `derive()` only emits that key
  // when a model ticks an OTA route -- so a model whose Host BOOTLOADER is
  // published to an EOL bench and never goes over the air was INVISIBLE to
  // this page, and kind `cluster` (four benches, no OTA) could not hold a
  // build at all. That is the exact failure D-123 exists to fix, and the
  // console was still enforcing it.
  const flashable = (pool.data?.models ?? []).filter((m) => m.status === "active"
    && Object.keys(componentsOf(m)).length > 0);

  // PRODUCT FIRST, THEN MODEL. Dattatray: "Before selecting model: we should
  // select the product/kind right? And asset wont be applicable in that list
  // (Products that are not assets!)"
  //
  // Right about the order, and the exclusion is stronger than "not an asset".
  // Checked against the templates: no asset kind declares a FOTA field —
  // and neither do `cell`, `motor` or `dcdc`. So "not an asset" is a
  // CONSEQUENCE, not the rule. The rule is "declares FOTA support", and
  // filtering on the real property rather than a proxy means a product that
  // gains or loses FOTA moves in and out of this list on its own.
  //
  // Derived from the model pool rather than from the templates, so the
  // picker can never offer a product whose model list would then be empty —
  // a dead end is worse than an absence.
  const fotaKinds = Array.from(new Set(flashable.map((m) => m.kind))).sort();
  const [kind, setKind] = useState("");
  const fotaModels = kind ? flashable.filter((m) => m.kind === kind) : flashable;

  const [modelId, setModelId] = useState("");
  const model = fotaModels.find((m) => m.version_id === modelId);
  // (NODE, ARTEFACT) IS THE IDENTITY (D-123, 0101). `software` was one picker
  // offering host/modem_cell/ble, which can only ever name an APP image -- so
  // a Host bootloader or a configuration file could be declared in the
  // catalogue and never uploaded. Dattatray: *"while uploading the firmware i
  // dont see how I can upload the Host controller bootlader and Host
  // controller Configuration"*.
  const [node, setNode] = useState("");
  const [artefact, setArtefact] = useState("");
  // The model's own declaration drives both pickers, so the form can only
  // offer what the catalogue says the board carries.
  const declared = componentsOf(model);
  const nodes = Object.keys(declared);
  const artefacts = declared[node] ?? [];
  const [version, setVersion] = useState(1);
  // WHO GETS THIS BUILD, AND HOW -- the reach matrix (D-126), per company: its
  // benches, and whether it also goes out over the air. This replaced three
  // separate pieces of state (a company list, a flat bench list, and nothing at
  // all for FOTA) because the three answers are per company and three parallel
  // flat lists cannot say "A has two benches, C has none".
  //
  // The bench OPTIONS are fetched inside `ReachMatrix`, once for every named
  // company rather than once per card -- `FirmwareSpaceOption` carries
  // `company_id`, so the grouping is free. Not from the products module: that
  // is behind GATE_PRODUCTS (1800), which no permission-matrix row grants, so
  // the list comes from FOTA's own route under the same publish capability.
  const [reach, setReach] = useState<ReachRow[]>([]);
  // Which company the reach was DEFAULTED for -- so the default is applied
  // once per scope, and an operator who then clears the row (empty = every
  // company) is not fought.
  const defaulted = useRef("");
  // ── Filters for the BUILDS list ──────────────────────────────────────
  // Dattatray: *"the list of builds shall also have same filters as overview
  // pages to show the list of software files."* Same four, same order, same
  // vocabulary as FOTA Overview and the rollout picker, so one mental model
  // covers all three screens.
  //
  // FILTERED IN THE BROWSER, DELIBERATELY. The list is O(builds) -- it is
  // already fetched whole, and it is small (one row per uploaded file, not
  // one per device), so a round trip per keystroke would buy nothing. That
  // reasoning is the OPPOSITE of the device list on Update, which is O(fleet)
  // and must filter SQL-side; the distinction is the point, not an
  // inconsistency.
  const { company: fCompany } = useCompanyScope();   // the app bar's (D-134)
  const [fKind, setFKind] = useState("");
  const [fModel, setFModel] = useState("");
  const [fSoftware, setFSoftware] = useState("");

  // Narrowed to who may actually receive this build (D-128).
  const uploadCompanies = useReachCompanies(
    kind || null, modelId || null, reach.map((r) => r.company_id),
    companies.data?.companies ?? []);
  // REACH FOLLOWS THE APP BAR (D-134). Dattatray: *"Upload a build section
  // shall automatically select REACH as per the company selection."* With
  // one company in scope the matrix opens with that company's row and FOTA
  // ticked; benches are left for the operator, because which bench gets a
  // build is a per-upload decision. At ALL companies nothing is assumed.
  useEffect(() => {
    if (!fCompany || defaulted.current === fCompany) return;
    if (!uploadCompanies.some((c) => c.company_id === fCompany)) return;
    defaulted.current = fCompany;
    setReach([{ company_id: fCompany, cps_ids: [], fota: true }]);
  }, [fCompany, uploadCompanies]);

  const allBuilds = fw.data?.firmware ?? [];
  const kindModels = flashable.filter((m) => !fKind || m.kind === fKind);
  const builds = allBuilds.filter((f) =>
    // An empty `companies` means released to ALL, so it matches every company
    // filter -- the same asymmetry the scope dialog spells out.
    (!fCompany || f.companies.length === 0 || f.companies.includes(fCompany))
    // `f.kind`, NOT `f.product_id`. Dattatray: *"as soon as select product i
    // dont see anything."* The dropdown offers catalog kinds ("cluster_c")
    // and `product_id` holds the model's NAME ("AE-CLUC-SEG-LCD-B-E1"), so
    // this comparison could never be true for any build. `kind` is resolved
    // server-side from `version_id`; a build that declares no model has
    // kind === null and cannot be claimed to be any product, so a Product
    // filter excludes it -- and the "N of M" count beside the row is what
    // makes that visible rather than mysterious.
    && (!fKind || f.kind === fKind)
    && (!fModel || f.version_id === fModel)
    && (!fSoftware || f.node === fSoftware));

  const [editScope, setEditScope] = useState<Firmware | null>(null);
  const [binFile, setBinFile] = useState<File | null>(null);

  const register = useMutation({
    mutationFn: async () => {
      if (!binFile) throw new Error("choose a .bin / .hex file");
      const form = new FormData();
      form.append("file", binFile);
      form.append("product_id", model ? model.name : "unknown");
      form.append("version", String(version));
      form.append("channel", "stable");
      // `software` is NOT sent: `register_firmware` derives the deprecated
      // mirror from (node, artefact) itself, and returns None for an artefact
      // with no legacy line -- which is what keeps a bootloader out of every
      // OTA path instead of mislabelling it as a host app.
      form.append("node", node);
      form.append("artefact", artefact);
      if (modelId) form.append("version_id", modelId);
      // ALWAYS SENT, even empty, and that is the difference between "this
      // client does not speak the matrix" and "the operator chose no
      // companies". The server reads an absent field as the former and keeps
      // its old flat path; an empty array is a real instruction (visible to
      // everyone, no bench, no air).
      form.append("reach", JSON.stringify(reach));
      return fota.registerFirmware(form);
    },
    onSuccess: () => { fb.ok(); setBinFile(null); setReach([]); defaulted.current = ""; setArtefact(""); void qc.invalidateQueries({ queryKey: ["fota", "fw"] }); },
    onError: fb.fail,
  });
  const removeFw = useMutation({
    mutationFn: (id: string) => fota.deleteFirmware(id),
    onSuccess: () => { fb.ok(); void qc.invalidateQueries({ queryKey: ["fota", "fw"] }); },
    onError: (e) => fb.fail(e instanceof ApiError && e.status === 409
      ? new ApiError(409, "FIRMWARE_IN_USE", "Can't delete - a rollout uses this firmware.") : e),
  });

  const modelName = (id: string | null) => fotaModels.find((m) => m.version_id === id)?.name
    ?? (pool.data?.models ?? []).find((m) => m.version_id === id)?.name ?? null;
  const companyName = (id: string | null) => (companies.data?.companies ?? [])
    .find((c) => c.company_id === id)?.name ?? null;
  const dim = <span style={{ color: "var(--t-dim)" }}>—</span>;
  const cols = useMemo<DataCol<Firmware>[]>(() => [
    // PRODUCT, THE COLUMN THAT WAS MISSING. Dattatray: *"The prodict colmn is
    // not there in the table."* The table could be filtered by product and
    // then did not say which product a row was -- so a filtered list and an
    // unfiltered one looked identical.
    { key: "kind", label: "Product", width: 150, priority: 2,
      render: (f) => f.kind ? kindLabel(tpl, f.kind) : dim,
      title: (f) => f.kind ?? "no model version declared - product unknown" },
    { key: "sw", priority: 2, label: "Software", width: 190, render: (f) => swName(f),
      title: (f) => f.node ? `node ${f.node} / artefact ${f.artefact}`
                           : "this build predates (node, artefact) and carries only the legacy software line" },
    // `model_name` comes from the SERVER now (resolved from version_id). The
    // browser-side lookup is kept only as a fallback for a row the API has not
    // re-fetched yet; the old code fell all the way back to eight hex
    // characters of a UUID, which is not a model name by any reading.
    { key: "model", label: "Subcomponent model", width: 180, priority: 1,
      render: (f) => f.model_name ?? (f.version_id ? modelName(f.version_id) : null) ?? dim,
      title: (f) => f.model_name ?? f.version_id ?? "no model version declared" },
    { key: "ver", priority: 1, label: "Version", width: 70, render: (f) => <span className="dev">v{f.version}</span> },
    { key: "file", priority: 2, label: "File", width: 180, mono: true, render: (f) => f.filename ?? dim },
    { key: "company", priority: 2, label: "Released to", width: 190, render: (f) => f.companies.length
        ? f.companies.map((c) => companyName(c) ?? c).join(", ")
        : "All companies",
      title: (f) => f.companies.map((c) => companyName(c) ?? c).join(", ") || "All companies" },
    { key: "spaces", priority: 2, label: "Benches", width: 150,
      // NOT "all" when empty. A build with no allocation reaches no tool, and
      // showing a blank here the way "All companies" shows for an empty
      // release list would read as the opposite of the truth.
      render: (f) => (f.cps_ids?.length ?? 0) ? `${f.cps_ids!.length} bench(es)` : "none",
      title: (f) => (f.cps_ids?.length ?? 0) ? f.cps_ids!.join(", ")
                                            : "not allocated - no tool is offered this build" },
    // STATED, NOT IMPLIED. Without this column "why was my rollout refused"
    // has no answer on the page that refused it, and an unticked build is the
    // DEFAULT for every new upload - so the common case is the one with no
    // explanation. Shows the COUNT rather than a yes/no, because the answer is
    // per company and "ENABLED" would be a lie about the other three.
    { key: "fota", priority: 2, label: "Over the air", width: 120,
      render: (f) => f.fota_companies.length
        ? <StatusChip st="on" map={{ on: {
            label: `${f.fota_companies.length} COMPAN${f.fota_companies.length === 1 ? "Y" : "IES"}`,
            color: "var(--teal)" } }} />
        : <StatusChip st="off" map={{ off: { label: "NONE", color: "var(--t-mute)" } }} />,
      title: (f) => f.fota_companies.length
        ? "rollouts allowed to: " + f.fota_companies.map((c) => companyName(c) ?? c).join(", ")
        : "no company is FOTA-enabled for this build - every rollout on it is refused. Edit to tick one" },
    { key: "size", priority: 3, label: "Size", width: 80, render: (f) => `${(f.size / 1024).toFixed(1)} KB` },
    { key: "sha", priority: 3, label: "SHA-256", width: 150, mono: true, title: (f) => f.sha256, render: (f) => f.sha256.slice(0, 12) + "…" },
    { key: "act", priority: 1, label: "", width: 140, render: (f) => (
      <span style={{ display: "flex", gap: 6 }}>
        <button className="btn ghost" onClick={(e) => { e.stopPropagation(); setEditScope(f); }}>Edit</button>
        <button className="btn red" onClick={(e) => { e.stopPropagation(); removeFw.mutate(f.id); }}>Delete</button>
      </span>) },
    // (kept last so the actions column stays at the right edge of the table)
    // `tpl` IS A DEPENDENCY, and leaving it out would have been a real bug
    // rather than a lint nit: the Product column labels kinds through
    // `kindLabel(tpl, …)`, the templates arrive on a later tick than the first
    // render, and a memo that never re-ran would have shown the raw
    // "cluster_c" for the life of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [pool.data, companies.data, tpl]);

  // ── The GROUPED view of the same filtered builds ──────────────────────
  // Dattatray: *"the Builds table has no colpsable view!"* — the third of the
  // three asks about this page (filters and bench allocation landed first).
  //
  // Product -> Model -> Controller is the same path the upload form walks and
  // the same four words the filter bar above uses, so the tree is a way of
  // READING the vocabulary the page already speaks, not a second one.
  //
  // THE LEAF DROPS THE THREE GROUPING COLUMNS. A row under
  // "Cluster > AE-CLUC-SEG-LCD-B-E1 > Host controller SW" does not need
  // Product, Model and Software columns repeating that for every version —
  // the header is the value. What is left is what actually varies: version,
  // file, scope, size, hash, actions.
  const [grouped, setGrouped] = useState(true);
  const leafCols = useMemo(
    () => cols.filter((c) => c.key !== "kind" && c.key !== "model" && c.key !== "sw"),
    [cols]);

  const tree = useMemo<TNode[]>(() => {
    // Insertion-ordered nesting, then sorted explicitly at each level — Map
    // order is the order builds happened to arrive, which is not an order.
    const byKind = new Map<string, Map<string, Map<string, Firmware[]>>>();
    for (const f of builds) {
      const k = f.kind ?? "";
      const m = f.version_id ?? "";
      const models = byKind.get(k) ?? new Map();
      byKind.set(k, models);
      const sws = models.get(m) ?? new Map();
      models.set(m, sws);
      const key = swKey(f);
      sws.set(key, [...(sws.get(key) ?? []), f]);
    }

    const count = (sws: Map<string, Firmware[]>) =>
      [...sws.values()].reduce((a, r) => a + r.length, 0);
    // One fixed controller order everywhere: the host is the board itself, the
    // planes hang off it. Alphabetical would put BLE first and read as a
    // ranking that is not true.
    // Host first, then BLE, then the modem; within a node, app before
    // bootloader before config. An unknown key sorts last rather than first.
    const NODE_RANK = ["host", "ble", "modem_cell"];
    const ART_RANK = ["app", "bootloader", "config"];
    const swRank = (k: string) => {
      const [n, a] = k.split("/");
      const ni = NODE_RANK.indexOf(n), ai = ART_RANK.indexOf(a);
      return (ni < 0 ? 99 : ni) * 10 + (ai < 0 ? 9 : ai);
    };

    return [...byKind.entries()]
      .map(([k, models]): TNode => ({
        key: `k:${k}`,
        // An empty key means the build declares no model version, so there is
        // no product to name. Say that rather than leaving a blank row.
        label: k ? kindLabel(tpl, k) : "No product declared",
        count: [...models.values()].reduce((a, sws) => a + count(sws), 0),
        kids: [...models.entries()]
          .map(([m, sws]): TNode => ({
            key: `m:${m}`,
            label: m
              ? (sws.values().next().value?.[0]?.model_name ?? modelName(m) ?? m.slice(0, 8))
              : "No model declared",
            count: count(sws),
            kids: [...sws.entries()]
              .sort((a, b) => swRank(a[0]) - swRank(b[0]))
              .map(([s, rows]): TNode => ({
                key: `s:${s}`,
                label: swName(rows[0]),
                count: rows.length,
                // Newest version first inside a controller: the question asked
                // of this leaf is almost always "what is the latest".
                rows: [...rows].sort((a, b) => b.version - a.version),
              })),
          }))
          .sort((a, b) => String(a.label).localeCompare(String(b.label))),
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builds, tpl, pool.data]);

  return (
    <div className="mpage on">
      <PageHead eyebrow="FOTA · Firmware" title="Firmware library"
        desc="A build = model + software line + version + file. Narrow by product, then pick the model - the Software options are exactly the FOTA methods its catalog ticks declare. Only products that declare FOTA appear. Scope to a company for customer-specific builds." />

      <div className="section-label">Upload a build</div>
      <div className="panel panel-pad" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 14 }}>
          <Field label="Product">
            <Select className="fill" value={kind}
              onChange={(e) => { setKind(e.target.value); setModelId(""); setNode(""); setArtefact(""); }}>
              {/* Only products that declare FOTA. Assets are absent because
                  none of them do - not because assets are excluded by name. */}
              <option value="">All flashable products</option>
              {fotaKinds.map((k) => (
                <option key={k} value={k}>{kindLabel(tpl, k)}</option>
              ))}
            </Select>
          </Field>
          <Field label="Model" req>
            <Select className="fill" value={modelId} onChange={(e) => { setModelId(e.target.value); setNode(""); setArtefact(""); }}>
              <option value="">{fotaModels.length ? "choose"
                : kind ? "no active model of this product declares FOTA"
                  : "no active model declares FOTA support"}</option>
              {fotaModels.map((m) => <option key={m.version_id} value={m.version_id}>{m.name} · {m.manufacturer}</option>)}
            </Select>
          </Field>
          <Field label="Node" req>
            <Select className="fill" value={node} disabled={!modelId}
              onChange={(e) => { setNode(e.target.value); setArtefact(""); }}>
              <option value="">{modelId ? "choose" : "model first"}</option>
              {nodes.map((n) => <option key={n} value={n}>{NODE_LABEL[n] ?? n}</option>)}
            </Select>
          </Field>
          <Field label="Software" req>
            {/* ARTEFACT, labelled "Software" because that is the operator's
                word for it. Offered only for the chosen node, because
                `SW_NODE_COMPONENTS` differs per node -- the host carries app,
                bootloader and config; the BLE module carries an app only. */}
            <Select className="fill" value={artefact} disabled={!node}
              onChange={(e) => setArtefact(e.target.value)}>
              <option value="">{node ? "choose" : "node first"}</option>
              {artefacts.map((a) => <option key={a} value={a}>{ARTEFACT_LABEL[a] ?? a}</option>)}
            </Select>
          </Field>
          <Field label="Version" req>
            <SearchInput className="fill" type="number" mono value={version} min={1}
              onChange={(e) => setVersion(Number(e.target.value))} />
          </Field>


          <Field label={FILE_KIND[artefact]?.label ?? "File"} req>
            {/* The accept list is a BROWSER HINT, not the rule -- a person can
                always choose "all files". `store_firmware` holds the real
                allowlist, and the catalogue decides what an artefact is. This
                just stops the common mistake of handing the bootloader slot a
                .bin. */}
            <FilePick accept={FILE_KIND[artefact]?.accept ?? ".bin,.hex"}
              label="Choose file" fileName={binFile?.name ?? ""} onFile={setBinFile} />
          </Field>
        </div>

        {/* REACH AT UPLOAD, AT THE SAME GRANULARITY AS EDIT (D-126).
            Dattatray: *"After uplaod teh edit option shall have similar upload
            granialruity and feedoem"* - so this is the same component the Edit
            dialog renders, not a simpler version of it. Outside the two-column
            field grid because a per-company matrix in a 220px control column
            is the unusable shape the grid is good at producing. */}
        <div style={{ marginTop: 6 }}>
          <div className="section-label flush">Reach</div>
          <ReachMatrix value={reach} onChange={setReach}
            companies={uploadCompanies} kind={kind || null} />
        </div>

        <ActionRow>
          {fb.errorNode}
          <button className="btn teal" disabled={!modelId || !node || !artefact || !binFile || register.isPending}
            onClick={() => register.mutate()}>Upload build</button>
        </ActionRow>
      </div>

      {editScope && (
        <EditScopeModal fw={editScope} companies={companies.data?.companies ?? []}
          onClose={() => setEditScope(null)}
          onSaved={() => { setEditScope(null); void qc.invalidateQueries({ queryKey: ["fota", "fw"] }); }} />
      )}

      <div className="section-label">Builds</div>
      <div className="panel panel-pad filterrow" style={{ marginBottom: 12 }}>
        <Field label="Product">
          <Select className="fill" value={fKind}
            onChange={(e) => { setFKind(e.target.value); setFModel(""); }}>
            <option value="">All products</option>
            {[...new Set(flashable.map((m) => m.kind))].map((k) => (
              <option key={k} value={k}>{kindLabel(tpl, k)}</option>
            ))}
          </Select>
        </Field>
        <Field label="Model">
          <Select className="fill" value={fModel} disabled={!fKind}
            onChange={(e) => setFModel(e.target.value)}>
            <option value="">{fKind ? "All models" : "product first"}</option>
            {kindModels.map((m) => (
              <option key={m.version_id} value={m.version_id}>{m.name} · {m.manufacturer}</option>
            ))}
          </Select>
        </Field>
        <Field label="Node">
          {/* BY NODE, not by `software`. The old list was built from
              `f.software`, which is null for a bootloader, so it offered a
              blank option that selected every bootloader and config at once. */}
          <Select className="fill" value={fSoftware}
            onChange={(e) => setFSoftware(e.target.value)}>
            <option value="">All nodes</option>
            {[...new Set(allBuilds.map((f) => f.node).filter(Boolean))].map((n) => (
              <option key={n as string} value={n as string}>{NODE_LABEL[n as string] ?? n}</option>
            ))}
          </Select>
        </Field>
        <span className="scope-act" style={{ alignSelf: "flex-end", paddingBottom: 6,
          display: "flex", alignItems: "center", gap: 10 }}>
          <span className="cap">{builds.length} of {allBuilds.length}</span>
          {/* `.fchip`, the filter chip the Connect and Connections pages
              already use for exactly this -- a two-state view switch. A third
              hand-rolled segmented control would be the duplication the UI
              laws call a review failure. */}
          <button className={`fchip${grouped ? " on" : ""}`} onClick={() => setGrouped(true)}
            title="Group the builds by product, model and controller">Grouped</button>
          <button className={`fchip${grouped ? "" : " on"}`} onClick={() => setGrouped(false)}
            title="One flat table of every build">Flat</button>
        </span>
      </div>
      {builds.length === 0 ? (
        <div className="panel panel-pad cap">
          {allBuilds.length
            ? "No builds match these filters."
            : "No builds yet - upload the first one above."}
        </div>
      ) : grouped ? (
        <div>
          {tree.map((n) => <TreeNode key={n.key} n={n} depth={0} leafCols={leafCols} />)}
        </div>
      ) : (
        <DataTable rows={builds} cols={cols} rowKey={(f) => f.id} pageSize={50} />
      )}
    </div>
  );
}


/** Edit a build's release scope: re-allocate to more companies, restrict, or
 *  open to all (empty selection). Rollouts outside the scope are refused. */
/** Edit a build's release scope: WHICH COMPANIES may have it, and WHICH
 *  BENCHES inside them are offered it.
 *
 *  Dattatray: *"edit option in the builds list does not allow allocating that
 *  firmware to different companies and spaces -- right now it gives a weird
 *  popup that only allocates the SW file to company (nothing about space in
 *  that company)."*
 *
 *  He was right, and only half the feature was missing: the API, the client
 *  wrapper and the `cps_ids` column in the list all existed, so the builds
 *  table SHOWED a Benches column that this dialog could not change. Edit the
 *  scope and the benches silently kept whatever the upload set.
 *
 *  THE TWO ARE DIFFERENT QUESTIONS AND BOTH MUST BE TRUE. A company release
 *  says *may this organisation have this build* and governs rollouts; a space
 *  allocation says *is this build for this bench* and governs what an EOL or
 *  UDS tool is offered. Empty company list means every company; empty bench
 *  list means NO tool is offered it -- the asymmetry is deliberate and is
 *  spelled out below, because "empty" meaning opposite things in two adjacent
 *  pickers is exactly the kind of thing a person gets wrong once.
 *
 *  Saved as TWO calls because they are two endpoints. The bench call runs
 *  only when the benches actually changed, so an unrelated company edit
 *  cannot disturb an allocation -- and a failure of either is surfaced rather
 *  than swallowed, since a half-applied scope is worse than a refused one.
 */
function EditScopeModal({ fw, companies, onClose, onSaved }: {
  fw: Firmware; companies: { company_id: string; name: string }[];
  onClose: () => void; onSaved: () => void;
}) {
  const fb = useMutationFeedback();
  // STRAIGHT FROM THE SERVER'S GROUPED MATRIX (D-126). Rebuilding it here from
  // `companies` + `cps_ids` + `fota_companies` is NOT POSSIBLE: a bench carries
  // no company on the wire, so grouping it under its company needs a join on
  // `company_product_spaces` that the browser does not have. `reach` is that
  // join, done once for the whole page by `firmware_reach_map`.
  //
  // A COPY, NOT THE PROP. Edits stay local until Save, so a background refetch
  // of the builds list cannot reshuffle the cards under the operator's cursor
  // or silently discard a tick they had just made.
  const [reach, setReach] = useState<ReachRow[]>(
    () => (fw.reach ?? []).map((r) => ({ ...r, cps_ids: [...r.cps_ids] })));

  // `fw.kind` is the CATALOG KIND, resolved server-side from `version_id`, and
  // it is not interchangeable with `product_id`. The bench picker was ALWAYS
  // EMPTY and nothing said so: it asked `/spaces?kind=<fw.product_id>` -- i.e.
  // kind="AE-CLUC-SEG-LCD-B-E1" -- while `publishable_spaces` matches
  // `company_product_spaces.kind`, which holds "cluster_c". The query was
  // valid, returned zero rows, and rendered as "No benches for these
  // companies": a wrong answer that looks exactly like a right one. Passed down
  // to `ReachMatrix`, which owns the bench query now.
  const kind = fw.kind;
  // Same narrowing as the upload form, from the same hook, so a build cannot
  // be EDITED into a reach it could not have been created with.
  const offered = useReachCompanies(kind, fw.version_id,
                                    reach.map((r) => r.company_id), companies);

  const save = useMutation({
    // ONE CALL. This was three, in sequence, and the middle one could fail:
    // companies written, benches refused, and a build left released to an
    // organisation whose bench never got it. `set_reach` writes all three
    // tables in one transaction.
    mutationFn: async () => { await fota.setFirmwareReach(fw.id, reach); },
    onSuccess: () => { fb.ok(); onSaved(); },
    onError: fb.fail,
  });

  return (
    // `xwide` (1400px). The reach grid is a company column, up to SEVEN bench
    // columns and the air column; at 600px it was unusable and at `wide`
    // (940px) it fitted only by scrolling sideways -- which hides the
    // right-hand columns in a control whose whole point is seeing every tick
    // at once. The upload form gets the full page width, so the dialog has to
    // be comparable or the same grid behaves differently in two places.
    <Modal size="xwide" title={`Reach - v${fw.version} (${fw.filename ?? fw.product_id})`} onClose={onClose}
      footer={<>
        {fb.errorNode}
        <button className="btn ghost" onClick={onClose}>Cancel</button>
        <button className="btn teal" disabled={save.isPending} onClick={() => save.mutate()}>Save scope</button>
      </>}>
      {/* THE SAME CONTROL THE UPLOAD FORM USES (D-126). Dattatray: *"After
          uplaod teh edit option shall have similar upload granialruity and
          feedoem"* - so it is literally the same component, not a second
          implementation of the same matrix. Two of those drift until a build
          can be created in a state the editor cannot represent. */}
      <ReachMatrix value={reach} onChange={setReach}
        companies={offered} kind={kind} />
    </Modal>
  );
}
